/**
 * Pure layer geometry: maps a resolved layer transform onto a quad in project pixel space
 * (origin top-left, y down). Shared by the compositor and on-canvas transform handles.
 */
import type { FitMode, ResolvedTransform } from '@ve/core';

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/** Corners in order top-left, top-right, bottom-right, bottom-left (before rotation). */
export type Quad = [Point, Point, Point, Point];

export interface UvRect {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

export interface LayerQuad {
  corners: Quad;
  uv: UvRect;
}

/** Size of a source after applying the fit mode against the canvas. */
export function fitSize(fit: FitMode, source: Size, canvas: Size): Size {
  if (source.width <= 0 || source.height <= 0) return { width: 0, height: 0 };
  switch (fit) {
    case 'contain': {
      const s = Math.min(canvas.width / source.width, canvas.height / source.height);
      return { width: source.width * s, height: source.height * s };
    }
    case 'cover': {
      const s = Math.max(canvas.width / source.width, canvas.height / source.height);
      return { width: source.width * s, height: source.height * s };
    }
    case 'stretch':
      return { width: canvas.width, height: canvas.height };
    case 'none':
      return { width: source.width, height: source.height };
  }
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Normalises crop fractions so that opposite edges never overlap (keeps at least 0.1% visible).
 */
export function normaliseCrop(crop: ResolvedTransform['crop']): ResolvedTransform['crop'] {
  let { left, right, top, bottom } = crop;
  left = clamp01(left);
  right = clamp01(right);
  top = clamp01(top);
  bottom = clamp01(bottom);
  const maxH = 0.999;
  if (left + right > maxH) {
    const k = maxH / (left + right);
    left *= k;
    right *= k;
  }
  if (top + bottom > maxH) {
    const k = maxH / (top + bottom);
    top *= k;
    bottom *= k;
  }
  return { left, right, top, bottom };
}

/**
 * Computes the on-canvas quad of a layer: fit → crop (keeps the remaining pixels in place) →
 * scale → rotate (clockwise, degrees) → translate (offset from canvas centre).
 */
export function computeLayerQuad(
  transform: ResolvedTransform,
  fit: FitMode,
  source: Size,
  canvas: Size,
): LayerQuad {
  const base = fitSize(fit, source, canvas);
  const crop = normaliseCrop(transform.crop);
  // Local rectangle (centred on the uncropped layer centre) of the visible region.
  const x0 = (-0.5 + crop.left) * base.width * transform.scaleX;
  const x1 = (0.5 - crop.right) * base.width * transform.scaleX;
  const y0 = (-0.5 + crop.top) * base.height * transform.scaleY;
  const y1 = (0.5 - crop.bottom) * base.height * transform.scaleY;
  const rad = (transform.rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const cx = canvas.width / 2 + transform.x;
  const cy = canvas.height / 2 + transform.y;
  // In a y-down space this matrix rotates clockwise for positive angles.
  const map = (x: number, y: number): Point => ({ x: cx + x * cos - y * sin, y: cy + x * sin + y * cos });
  return {
    corners: [map(x0, y0), map(x1, y0), map(x1, y1), map(x0, y1)],
    uv: { u0: crop.left, v0: crop.top, u1: 1 - crop.right, v1: 1 - crop.bottom },
  };
}

/** True if a point lies inside a convex quad (corners in winding order). */
export function pointInQuad(p: Point, quad: Quad): boolean {
  let sign = 0;
  for (let i = 0; i < 4; i++) {
    const a = quad[i]!;
    const b = quad[(i + 1) % 4]!;
    const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
    if (cross !== 0) {
      const s = Math.sign(cross);
      if (sign === 0) sign = s;
      else if (s !== sign) return false;
    }
  }
  return true;
}

/** Parses `#rrggbb` / `#rrggbbaa` into premultiplied RGBA floats. */
export function parseColor(hex: string): [number, number, number, number] {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const a = h.length >= 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
  if ([r, g, b, a].some((v) => Number.isNaN(v))) return [0, 0, 0, 1];
  return [r * a, g * a, b * a, a];
}

/** Like {@link parseColor}, but in linear light (the compositor's working space). */
export function parseLinearColor(hex: string): [number, number, number, number] {
  const [r, g, b, a] = parseColor(hex);
  const lin = (premultiplied: number) => {
    const c = a > 0 ? premultiplied / a : 0;
    return (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4) * a;
  };
  return [lin(r), lin(g), lin(b), a];
}
